import { toast } from '@affine/component';
import { getStoreManager } from '@affine/core/blocksuite/manager/store';
import { AppSidebarService } from '@affine/core/modules/app-sidebar';
import {
  DEFAULT_DOC_MODE,
  DocsService,
  isCreateDocPermissionError,
} from '@affine/core/modules/doc';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { getStreamLainWorkspaceSchema } from '@affine/core/modules/workspace';
import { type DocMode } from '@blocksuite/affine/model';
import type { Workspace } from '@blocksuite/affine/store';
import { useServices } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import type { DocCreateOptions } from '../../modules/doc/types';

export const usePageHelper = (docCollection: Workspace) => {
  const { docsService, workbenchService, appSidebarService } = useServices({
    DocsService,
    WorkbenchService,
    AppSidebarService,
  });
  const workbench = workbenchService.workbench;
  const docRecordList = docsService.list;
  const appSidebar = appSidebarService.sidebar;

  const createPageAndOpen = useCallback(
    (
      mode?: DocMode,
      options: {
        at?: 'new-tab' | 'tail' | 'active';
        show?: boolean;
        targetFolderId?: DocCreateOptions['targetFolderId'];
        createInWorkspaceRoot?: DocCreateOptions['createInWorkspaceRoot'];
        title?: DocCreateOptions['title'];
      } = {
        at: 'active',
        show: true,
      }
    ) => {
      const {
        at = 'active',
        show = true,
        targetFolderId,
        createInWorkspaceRoot,
        title,
      } = options;
      appSidebar.setHovering(false);
      let page;
      try {
        page = docsService.createDoc({
          targetFolderId,
          createInWorkspaceRoot,
          title,
          userInitiated: true,
          funnelCoreJobType: 'document_created',
        });
      } catch (error) {
        if (isCreateDocPermissionError(error)) {
          toast('Keine Berechtigung zum Erstellen von Dokumenten.');
        }
        throw error;
      }

      if (mode) {
        docRecordList.doc$(page.id).value?.setPrimaryMode(mode);
      }

      if (show !== false) {
        workbench.openDoc(
          {
            docId: page.id,
            mode: docRecordList.getPrimaryMode(page.id) ?? DEFAULT_DOC_MODE,
          },
          {
            at,
            show,
          }
        );
      }
      return page;
    },
    [appSidebar, docRecordList, docsService, workbench]
  );

  const createEdgelessAndOpen = useCallback(
    (
      options: {
        at?: 'new-tab' | 'tail' | 'active';
        show?: boolean;
        targetFolderId?: DocCreateOptions['targetFolderId'];
        createInWorkspaceRoot?: DocCreateOptions['createInWorkspaceRoot'];
        title?: DocCreateOptions['title'];
      } = {
        at: 'active',
        show: true,
      }
    ) => {
      return createPageAndOpen('edgeless', options);
    },
    [createPageAndOpen]
  );

  const importFileAndOpen = useMemo(
    () => async () => {
      const { showImportModal } =
        await import('@blocksuite/affine/widgets/linked-doc');
      const { promise, resolve, reject } =
        Promise.withResolvers<
          Parameters<
            NonNullable<Parameters<typeof showImportModal>[0]['onSuccess']>
          >[1]
        >();
      const onSuccess = (
        pageIds: string[],
        options: { isWorkspaceFile: boolean; importedCount: number }
      ) => {
        resolve(options);
        toast(
          `Successfully imported ${options.importedCount} Page${
            options.importedCount > 1 ? 's' : ''
          }.`
        );
        if (options.isWorkspaceFile) {
          workbench.openAll();
          return;
        }

        if (pageIds.length === 0) {
          return;
        }
        const pageId = pageIds[0];
        workbench.openDoc(pageId);
      };
      showImportModal({
        collection: docCollection,
        schema: getStreamLainWorkspaceSchema(),
        extensions: getStoreManager().config.init().value.get('store'),
        onSuccess,
        onFail: message => {
          reject(new Error(message));
        },
      });
      return await promise;
    },
    [docCollection, workbench]
  );

  return useMemo(() => {
    return {
      createPage: (
        mode?: DocMode,
        options?: {
          at?: 'new-tab' | 'tail' | 'active';
          show?: boolean;
          targetFolderId?: DocCreateOptions['targetFolderId'];
          createInWorkspaceRoot?: DocCreateOptions['createInWorkspaceRoot'];
          title?: DocCreateOptions['title'];
        }
      ) => createPageAndOpen(mode, options),
      createEdgeless: createEdgelessAndOpen,
      importFile: importFileAndOpen,
    };
  }, [createEdgelessAndOpen, createPageAndOpen, importFileAndOpen]);
};
